import { apiErrorList } from 'Services/restAdviser'
import { rechargeWithCreditCard as cardRecharge, rechargeWithBoleto as boletoRecharge, rechargeWithPix as pixRecharge } from 'Api/walletStatementApi'
import { getBillingData } from 'Api/billingApi'
import { getBalance } from 'Api/balanceApi'
import { getPendingBoletos } from 'Api/walletStatementApi'
import { parsePendingBoleto } from 'Parsers/walletStatementParser'
import { getRechargeState, enableRecharge } from 'Api/autoRechargeApi'
import { parseRechargeInfo } from 'Parsers/autoRechargeParser'

const state = {
  requirements: {
    billing: true,
  },

  requesting: {
    recharge: false,
    boleto: false,
    balance: false,
    requirements: false,
    autoInfo: false,
    autoEnable: false,
  },

  balance: {
    value: '',
    format: '',
  },

  hasActiveCampaign: false,
  hasAutomaticRecharge: false,

  selectedPayment: {
    key: null,
    text: null,
  },

  selectedAmount: {
    key: null,
    text: null,
    value: null,
  },

  paymentTypes: [
    {
    key: 'novo-cartao',
    text: 'Novo cartão de crédito'
  },{
    key: 'boleto',
    text: 'Boleto bancário'
  }, {
    key: 'pix',
    text: 'PIX'
  }],

  rechargeAmount: [{
    key: '85',
    text: '85 CoteiCréditos',
    value: 85
  },{
    key: '170',
    text: '170 CoteiCréditos',
    value: 170
  },{
    key: '255',
    text: '255 CoteiCréditos',
    value: 255
  },{
    key: 'custom',
    text: 'Outro valor',
    value: null
  }],

  autoInfo: {
    recharge: {
      amount: 0,
      trigger: 0,
      since: '',
      active: false,
    },
    card: {
      holder: '',
      digits: '',
    },
  },

  autoTrigger: {
    amount: 30,
    min: 30,
    max: 3000,
  },

  boletoList: [],

  errors: []
}

const getters = {
  getCustomRecharge: state => {
    return state.rechargeAmount.find(amount => amount.key === 'custom')
  },

  isRechargePaymentSelected: state => key => {
    return state.selectedPayment.key === key
  },
  getBalanceAsCredits: state => {
    return  (state.balance.value % 1 === 0) ? state.balance.value.toLocaleString('pt-Br') : parseFloat(state.balance.value.toFixed(2)).toLocaleString('pt-Br')
  }
}

const mutations = {
  request_start(state, type) {
    state.requesting[type] = true
    state.errors.length = 0
  },

  request_success(state, type) {
    state.requesting[type] = false
  },

  request_failure(state, { type, errors }) {
    state.requesting[type] = false
    state.errors = errors
  },

  set_balance(state, balance) {
    state.balance = Object.assign(state.balance, balance)
  },

  set_active_campaign(state, campaign) {
    state.hasActiveCampaign = campaign
  },

  set_has_automatic_recharge(state, campaign) {
    state.hasAutomaticRecharge = campaign
  },

  set_requirements(state, requirements) {
    Object.assign(state.requirements, requirements)
  },

  set_recharge_payment(state, selectedPayment) {
    Object.assign(state.selectedPayment, selectedPayment)
  },

  set_recharge_amount(state, selectedAmount) {
    Object.assign(state.selectedAmount, selectedAmount)
  },

  set_recharge_custom(state, rechargeAmount) {
    state.rechargeAmount = rechargeAmount
  },

  set_auto_trigger(state, autoTrigger) {
    Object.assign(state.autoTrigger, autoTrigger)
  },

  set_boleto_list(state, boletoList) {
    state.boletoList = boletoList
  },

  set_auto_recharge_info(state, autoInfo) {
    state.autoInfo = Object.assign(state.autoInfo, autoInfo)
  }
}

const actions = {
  async checkBalance({ commit }) {
    commit('request_start', 'balance')

    try {
      const { data } = await getBalance()

      const balance = {
        value: data.amount,
        format: data.amountFormat      }

      commit('set_balance', balance)
      commit('set_active_campaign', data.hasActiveCampaign)
      commit('set_has_automatic_recharge', data.hasAutomaticRecharge)

      commit('request_success', 'balance')

      return data
    } catch (err) {
      commit('request_failure', 'balance')

      throw err
    }
  },

  async checkRequirements({ commit }) {
    commit('request_start', 'requirements')

    try {
      const { data } = await getBillingData()

      const billing = data !== ''
      commit('set_requirements', { billing })

      commit('request_success', 'requirements')

      return data
    } catch (err) {
      commit('request_failure', { type: 'requirements', errors: [] })

      throw err
    }
  },

  async getAutoRechargeState({ commit, state }) {
    commit('request_start', 'autoInfo')

    try {
      const { data } = await getRechargeState()

      const autoInfo = data
        ? parseRechargeInfo(data)
        : state.autoInfo

      commit('set_auto_recharge_info', autoInfo)
      commit('request_success', 'autoInfo')

      return true
    } catch (err) {
      commit('request_failure', 'autoInfo')

      throw err
    }
  },

  selectRechargePayment({ commit }, payment) {
    commit('set_recharge_payment', payment)
  },

  selectRechargeAmount({ dispatch, commit }, recharge) {
    const maxAmount = Math.floor((recharge.value * 0.75) / 5) * 10

    dispatch('updateAutoTrigger', {
      amount: maxAmount,
      max: maxAmount,
    })

    commit('set_recharge_amount', recharge)
  },

  updateRechargeCustom({ commit, getters, state }, amount) {
    const customAmount = { ...getters.getCustomRecharge, ...amount }

    const rechargeAmount = state.rechargeAmount.map(amount => amount.key === customAmount.key ? customAmount : amount)

    commit('set_recharge_custom', rechargeAmount)
  },

  updateAutoTrigger({ commit }, newTrigger){
    commit('set_auto_trigger', newTrigger)
  },

  async enableAutoRecharge({ commit, state }, cardId) {
    commit('request_start', 'autoEnable')

    try {
      const { selectedAmount, autoTrigger } = state
      const result = await enableRecharge(selectedAmount.value, autoTrigger.amount, cardId)

      commit('request_success', 'autoEnable')

      return result
    } catch (err) {
      commit('request_failure', 'autoEnable')

      throw err
    }
  },

  async rechargeWithCreditCard({ commit, state }, info) {
    const { selectedAmount } = state
    commit('request_start', 'recharge')
    try {
      const { data } = await cardRecharge(selectedAmount.value, info.creditCardId, info.selectedRecharge)
      commit('request_success', 'recharge')

      return data
    } catch (err) {
      const errors = apiErrorList(err)
      commit('request_failure', { errors, type: 'recharge' })

      throw err
    }
  },

  async rechargeWithBoleto({ commit, state }) {
    const { selectedAmount } = state
    commit('request_start', 'recharge')

    try {
      const { data } = await boletoRecharge(selectedAmount.value)
      commit('request_success', 'recharge')

      return data
    } catch (err) {
      const errors = apiErrorList(err)
      commit('request_failure', { errors, type: 'recharge' })

      throw err
    }
  },

  async rechargeWithPix({ commit, state }) {
    const { selectedAmount } = state
    commit('request_start', 'recharge')

    try {
      const { data } = await pixRecharge(selectedAmount.value)
      commit('request_success', 'recharge')

      return data
    } catch (err) {
      const errors = apiErrorList(err)
      commit('request_failure', { errors, type: 'recharge' })

      throw err
    }
  },

  async getBoletoList({ commit }) {
    commit('request_start', 'boleto')

    try {
      const { data } = await getPendingBoletos()
      const boletoList = data.map(parsePendingBoleto)

      commit('set_boleto_list', boletoList)
      commit('request_success', 'boleto')

      return data
    } catch (err) {
      commit('request_failure', 'boleto')

      throw err
    }
  }
}

export default {
  namespaced: true,

  getters,
  state,
  actions,
  mutations
}